/**
 * Hook: useProspect
 * Maneja datos del prospecto con debounced save que ACUMULA cambios
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import useCRMStore from '../store/useCRMStore'
import { prospectAPI } from '../services/api'

export const useProspect = (phone, { onSaveComplete } = {}) => {
  const {
    prospects,
    setProspect,
    updateProspect,
    shouldFetchProspect,
    sessionExpired
  } = useCRMStore()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Acumulador de cambios pendientes
  const pendingChanges = useRef({})
  const saveTimeout = useRef(null)

  const prospectData = prospects[phone] || null

  // Flush pending changes on unmount or phone change
  useEffect(() => {
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current)
        saveTimeout.current = null
      }
      // Fire pending save immediately (fire-and-forget, completes even after unmount)
      const changes = { ...pendingChanges.current }
      if (phone && Object.keys(changes).length > 0) {
        pendingChanges.current = {}
        console.log('Flushing pending changes on unmount:', Object.keys(changes).join(', '))
        prospectAPI.updateProspect(phone, changes).catch(err => {
          console.error('Error flushing pending changes:', err)
        })
      }
    }
  }, [phone])

  // Cargar prospecto cuando cambia el phone
  useEffect(() => {
    if (phone && shouldFetchProspect(phone)) {
      loadProspect()
    }
    // Limpiar cambios pendientes al cambiar de contacto
    pendingChanges.current = {}
  }, [phone])

  const loadProspect = async () => {
    if (!phone || sessionExpired) return

    try {
      setLoading(true)
      setError(null)
      const data = await prospectAPI.getProspect(phone)
      setProspect(phone, data)
    } catch (err) {
      setError(err.message)
      console.error('Error loading prospect:', err)
    } finally {
      setLoading(false)
    }
  }

  // Función que ejecuta el guardado real
  const executeSave = useCallback(async () => {
    if (!phone || sessionExpired) return

    const changes = { ...pendingChanges.current }
    if (Object.keys(changes).length === 0) return

    // Limpiar cambios pendientes ANTES de guardar
    pendingChanges.current = {}

    try {
      setSaving(true)
      const savedPhone = phone // Capturar el phone actual antes del await
      await prospectAPI.updateProspect(savedPhone, changes)
      console.log('✅ Auto-guardado exitoso:', Object.keys(changes).join(', '))
      // Notificar al padre con el phone guardado (no el actual que puede haber cambiado)
      if (onSaveComplete) onSaveComplete(savedPhone, changes)
    } catch (err) {
      console.error('❌ Error en auto-guardado:', err)
      // Restaurar cambios pendientes si falló
      pendingChanges.current = { ...pendingChanges.current, ...changes }
    } finally {
      setSaving(false)
    }
  }, [phone, sessionExpired])

  // Programar guardado con debounce (acumula cambios)
  const scheduleSave = useCallback((updates) => {
    // Acumular cambios
    pendingChanges.current = { ...pendingChanges.current, ...updates }

    // Cancelar timeout anterior
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current)
    }

    // Programar nuevo guardado en 2 segundos
    saveTimeout.current = setTimeout(() => {
      executeSave()
    }, 2000)
  }, [executeSave])

  const updateField = (field, value) => {
    if (!phone) return

    // 1. Actualizar local inmediatamente (optimistic)
    updateProspect(phone, { [field]: value })

    // 2. Programar guardado (debounced, acumulativo)
    scheduleSave({ [field]: value })
  }

  const updateFields = (updates) => {
    if (!phone) return

    // 1. Actualizar local inmediatamente
    updateProspect(phone, updates)

    // 2. Programar guardado (debounced, acumulativo)
    scheduleSave(updates)
  }

  const saveNow = async () => {
    if (!phone || !prospectData || sessionExpired) return

    // Cancelar cualquier guardado automático pendiente
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current)
      saveTimeout.current = null
    }
    const changes = { ...pendingChanges.current }
    pendingChanges.current = {}

    if (Object.keys(changes).length === 0) return { success: true }

    try {
      setSaving(true)
      const savedPhone = phone // Capturar el phone actual
      await prospectAPI.updateProspect(savedPhone, changes)
      console.log('✅ Guardado manual exitoso')
      // Notificar al padre con el phone guardado
      if (onSaveComplete) onSaveComplete(savedPhone, changes)
      return { success: true }
    } catch (err) {
      console.error('❌ Error en guardado manual:', err)
      return {
        success: false,
        error: err.response?.data?.error || 'Error al guardar'
      }
    } finally {
      setSaving(false)
    }
  }

  return {
    prospect: prospectData,
    loading,
    error,
    saving,
    updateField,
    updateFields,
    saveNow,
    refresh: loadProspect
  }
}
